import React from 'react';
import { Header, ST, SUPPORT_CHANNELS } from './Chrome';
import { SiteFooterV2 } from './Footers';
import agentsFaq from './agentsFaq';

// `plain` drops the white cocard and paints the body gray — content sits
// directly on the page ground (used by /about). nolrail suppresses the
// 320px rail column the .body grid otherwise reserves.
function LegalShell({ me, currency, onCurrency, title, plain, children }) {
  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero">
        <h1 className="hero-title">{title}</h1>
      </section>
      <div className={'body nolrail' + (plain ? ' plainbody' : '')}>
        <main className="main legal">
          {plain ? children : <div className="cocard">{children}</div>}
        </main>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Terms of Service
//
// lib/legalContent.js has a plain-HTML mirror of this content, server-
// rendered into #root for crawlers that don't run JavaScript. Update both
// together when these terms change.
// ---------------------------------------------------------------------------
export function TermsPage({ me, currency, onCurrency }) {
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="Terms of Service">
      <p className="legal-updated">Last updated October 2, 2026</p>

      <h2>Agreement to Terms</h2>
      <p>
        These Terms of Service constitute a legally binding agreement made between you, whether
        personally or on behalf of an entity ("you") and Vivid Productions, LLC, doing business
        as MobileTopup.com (<b>"MobileTopup.com"</b>, <b>"we"</b>, <b>"us"</b>, or <b>"our"</b>),
        concerning your access to and use of the <a href="https://mobiletopup.com">https://mobiletopup.com</a> website,
        associated mobile applications, the Partners Portal at partners.mobiletopup.com, the Eve
        AI agent at agent.mobiletopup.com, and any other media form, media channel, or mobile
        application related, linked, or otherwise connected thereto (collectively, the "Site").
      </p>
      <p>
        We are registered in Washington, United States, with our registered office
        at 100 N Howard St Ste R, Spokane, WA 99201-0508. Services in Thailand are facilitated
        through our affiliate, Vivid Productions, Co. Ltd., located at Siripanich Building
        5th Floor, Room 5I, No. 191 Moo 2, Huaykaew Rd, T. Suthep, Muang, Chiang Mai 50200,
        Thailand.
      </p>
      <p>
        You agree that by accessing the Site, you have read, understood, and agreed to be bound
        by all of these Terms of Service. <b>If you do not agree with all of these Terms of
        Service, then you are expressly prohibited from using the Site and you must discontinue
        use immediately.</b>
      </p>
      <p>
        We reserve the right to make changes or modifications to these Terms of Service from
        time to time. We will alert you about any changes by updating the "Last updated" date.
        You will be subject to, and will be deemed to have been made aware of and to have
        accepted, the changes in any revised Terms by your continued use of the Site after
        the date such revised Terms are posted.
      </p>
      <p>
        The Site is intended for users who are at least 18 years old. Persons under the age
        of 18 are not permitted to use or register for the Site.
      </p>

      <h2>Services Provided</h2>
      <p>MobileTopup.com provides the following services:</p>
      <ul>
        <li><b>Prepaid mobile topup</b> — instant delivery of airtime credit to prepaid mobile phones in 100+ countries via Ding, Reloadly, and local provider integrations.</li>
        <li><b>eSIM data plans</b> — prepaid eSIM data plans for 200+ destinations, delivered digitally via QR code.</li>
        <li><b>Gift cards, game credit, and utility payments</b> — digital delivery of vouchers and credits.</li>
        <li><b>Thailand topup services</b> — AIS, DTAC, and TrueMove H topups facilitated through Vivid Productions, Co. Ltd. and its authorized telecom partners.</li>
        <li><b>Partners Portal</b> — a wholesale platform for trusted partners to purchase topups and eSIMs programmatically via API or web interface.</li>
        <li><b>Eve AI agent</b> — an AI-powered customer support and ordering agent accessible via WhatsApp, Telegram, and web chat.</li>
      </ul>

      <h2>Intellectual Property Rights</h2>
      <p>
        Unless otherwise indicated, the Site is our proprietary property and all source code,
        databases, functionality, software, website designs, audio, video, text, photographs,
        and graphics on the Site (collectively, the "Content") and the trademarks, service
        marks, and logos contained therein (the "Marks") are owned or controlled by us or
        licensed to us. The Content and the Marks are provided on the Site "AS IS" for your
        information and personal use only. No part of the Site may be copied, reproduced,
        aggregated, republished, uploaded, posted, publicly displayed, encoded, translated,
        transmitted, distributed, sold, licensed, or otherwise exploited for any commercial
        purpose whatsoever, without our express prior written permission.
      </p>
      <p>
        You are granted a limited license to access and use the Site and to download or print
        a copy of any portion of the Content to which you have properly gained access solely
        for your personal, non-commercial use.
      </p>

      <h2>User Representations</h2>
      <p>By using the Site, you represent and warrant that:</p>
      <ol>
        <li>All registration information you submit will be true, accurate, current, and complete.</li>
        <li>You will maintain the accuracy of such information and promptly update it as necessary.</li>
        <li>You have the legal capacity and agree to comply with these Terms.</li>
        <li>You are not a minor in the jurisdiction in which you reside.</li>
        <li>You will not access the Site through automated or non-human means, whether through a bot, script, or otherwise, except through our documented APIs.</li>
        <li>You will not use the Site for any illegal or unauthorized purpose.</li>
        <li>Your use of the Site will not violate any applicable law or regulation.</li>
      </ol>
      <p>
        If you provide any information that is untrue, inaccurate, not current, or incomplete,
        we have the right to suspend or terminate your account and refuse any and all current
        or future use of the Site.
      </p>

      <h2>User Registration</h2>
      <p>
        You may be required to register with the Site. You may register using your email address,
        or via Google or Facebook social login. You agree to keep your password confidential and
        will be responsible for all use of your account and password. We reserve the right to
        remove, reclaim, or change a username you select if we determine, in our sole discretion,
        that such username is inappropriate, obscene, or otherwise objectionable.
      </p>

      <h2>Products</h2>
      <p>
        All products are subject to availability. We reserve the right to discontinue any products
        at any time for any reason. Prices for all products are subject to change. Product delivery
        is subject to the availability of the receiving carrier's network and systems.
      </p>

      <h2>Purchases and Payment</h2>
      <p>We accept the following forms of payment:</p>
      <ul>
        <li>Visa, Mastercard, JCB, American Express (via Stripe and Omise)</li>
        <li>Thai Banks (via Omise)</li>
        <li>Alipay</li>
        <li>PayPal</li>
        <li>Philippine Peso payments (via Xendit, in partnership with Seagulls E-load, Prepaid Cards) — including e-wallets (GrabPay, Maya, ShopeePay) and direct debit from Philippine banks (BPI, UnionBank, RCBC, Chinabank)</li>
        <li>Account balance</li>
        <li>USDC stablecoin (Partners Portal)</li>
      </ul>
      <p>
        MobileTopup.com facilitates payment and topup in Thailand through Vivid Productions,
        Co. Ltd. and its third-party telecom, credit card processing, and banking partners.
      </p>
      <p>
        You agree to provide current, complete, and accurate purchase and account information
        for all purchases. You further agree to promptly update account and payment information
        so that we can complete your transactions and contact you as needed.
      </p>
      <p>
        You agree to pay all charges at the prices then in effect for your purchases, and you
        authorize us to charge your chosen payment provider for any such amounts upon placing
        your order. We reserve the right to correct any errors or mistakes in pricing, even if
        we have already requested or received payment.
      </p>
      <p>
        We reserve the right to refuse any order placed through the Site. We may, in our sole
        discretion, limit or cancel quantities purchased per person, per household, or per order.
        We reserve the right to limit or prohibit orders that, in our sole judgment, appear to
        be placed by dealers, resellers, or distributors without authorization.
      </p>

      <h2>Refunds Policy</h2>
      <p>
        Our full refund policy is available on our dedicated <a href="/refund-policy">Refund Policy</a> page.
        In summary: successfully delivered topups, eSIM plans, and gift card codes are final. Failed
        deliveries are refunded automatically. Unused eSIMs (zero data consumed) receive a full refund.
      </p>

      <h2 id="referrals">Refer-a-Friend Program</h2>
      <p>
        Customers with a verified email and at least five paid orders may share a personal invite
        link. When a new customer creates an account through that link (or enters the code at
        signup) and their first order of at least ฿300 or $10 is delivered, both the inviter and the
        new customer receive $1 of USD wallet credit after a 10-day hold. Credit is for purchases on
        the Site only; it has no cash value and cannot be withdrawn or transferred.
      </p>
      <ul>
        <li>The new customer must order within 90 days of signing up. Existing accounts cannot be attributed.</li>
        <li>No credit is issued, and issued credit may be reversed, if the qualifying order is refunded, disputed or
          charged back, if either account is suspended, or if we determine the accounts belong to the same person or
          household (including top-ups to a number the inviter has already topped up).</li>
        <li>Each inviter may earn at most 20 referral rewards per calendar month. Wholesale partners and partner-portal
          members are not eligible.</li>
        <li>We may review any referral manually, void rewards obtained through misuse, and change or end the program at
          any time.</li>
      </ul>

      <h2>Prohibited Activities</h2>
      <p>
        You may not access or use the Site for any purpose other than that for which we make
        the Site available. As a user of the Site, you agree not to:
      </p>
      <ol>
        <li>Systematically retrieve data or other content from the Site to create or compile a collection, compilation, database, or directory without written permission from us.</li>
        <li>Make any unauthorized use of the Site, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email, or creating user accounts by automated means or under false pretenses.</li>
        <li>Circumvent, disable, or otherwise interfere with security-related features of the Site.</li>
        <li>Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.</li>
        <li>Interfere with, disrupt, or create an undue burden on the Site or the networks or services connected to the Site.</li>
        <li>Use any automated system, including scripts, bots, spiders, scrapers, or data mining tools, except through our documented APIs.</li>
        <li>Upload or transmit viruses, Trojan horses, or other harmful material.</li>
        <li>Use the Site in a manner inconsistent with any applicable laws or regulations.</li>
        <li>Resell the Site's services without authorization.</li>
        <li>Attempt to bypass any fraud prevention, rate limiting, or anti-abuse measures.</li>
      </ol>

      <h2>Partner API Usage</h2>
      <p>
        Partners accessing our services via the Partner API v1 are subject to additional terms:
      </p>
      <ul>
        <li>API keys are confidential and must not be shared or exposed in client-side code.</li>
        <li>API usage is subject to rate limits (60 requests per minute per key).</li>
        <li>Partners are responsible for all activity conducted through their API keys.</li>
        <li>We reserve the right to revoke API access at any time for violations of these terms.</li>
      </ul>

      <h2>Purchases Through AI Agents</h2>
      <ul>
        <li>You may connect an AI assistant or other automated agent (“Agent”) to MobileTopup to search our products, check prices and prepare a checkout on your behalf.</li>
        <li>An Agent cannot complete a purchase. Every order is confirmed and paid by you on mobiletopup.com.</li>
        <li>You are responsible for the details an Agent enters for you, including the recipient number, product, amount and email address. Check them before you pay.</li>
        <li>Agent keys belong to your account. Keep them private, and do not share, resell or use them to scrape or overload our services. You are responsible for all activity under your key.</li>
        <li>Agent keys are rate-limited and may be suspended or revoked at our discretion.</li>
        <li>We record Agent activity, including the key, the Agent’s reported identity and the checkouts it prepares, to operate the service and prevent fraud. Checkout links that do not lead to an order are deleted after 90 days.</li>
        <li>Prices quoted to an Agent are valid only until the checkout link expires.</li>
      </ul>

      <h2>Dispute Resolution</h2>
      <h3>Informal Negotiations</h3>
      <p>
        To expedite resolution and control the cost of any dispute, the Parties agree to first
        attempt to negotiate any Dispute informally for at least ninety (90) days before
        initiating arbitration. Such informal negotiations commence upon written notice from
        one Party to the other Party.
      </p>
      <h3>Binding Arbitration</h3>
      <p>
        If the Parties are unable to resolve a Dispute through informal negotiations, the Dispute
        will be finally and exclusively resolved by binding arbitration. The arbitration shall be
        commenced and conducted under the Commercial Arbitration Rules of the American Arbitration
        Association ("AAA") and, where appropriate, the AAA's Supplementary Procedures for
        Consumer Related Disputes, both of which are available at <a href="https://www.adr.org">www.adr.org</a>.
        Except where otherwise required by the applicable AAA rules or applicable law, the
        arbitration will take place in Spokane County, Washington.
      </p>
      <p>
        If for any reason, a Dispute proceeds in court rather than arbitration, the Dispute
        shall be commenced or prosecuted in the state and federal courts located in Washington
        State, and the Parties hereby consent to jurisdiction in such courts.
      </p>
      <h3>Restrictions</h3>
      <p>
        The Parties agree that any arbitration shall be limited to the Dispute between the
        Parties individually. No arbitration shall be joined with any other proceeding, and
        there is no right or authority for any Dispute to be arbitrated on a class-action
        basis.
      </p>

      <h2>Disclaimer</h2>
      <p>
        THE SITE IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE THAT YOUR USE OF
        THE SITE AND SERVICES WILL BE AT YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY
        LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SITE AND
        YOUR USE THEREOF, INCLUDING, WITHOUT LIMITATION, THE IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO
        WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR COMPLETENESS OF THE SITE'S CONTENT
        AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY FOR ANY (1) ERRORS, MISTAKES, OR
        INACCURACIES OF CONTENT AND MATERIALS, (2) PERSONAL INJURY OR PROPERTY DAMAGE RESULTING
        FROM YOUR ACCESS TO AND USE OF THE SITE, (3) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR
        SECURE SERVERS AND/OR ANY PERSONAL OR FINANCIAL INFORMATION STORED THEREIN, (4) ANY
        INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SITE, OR (5) ANY BUGS, VIRUSES,
        TROJAN HORSES, OR THE LIKE WHICH MAY BE TRANSMITTED TO OR THROUGH THE SITE BY ANY THIRD
        PARTY.
      </p>

      <h2>Limitations of Liability</h2>
      <p>
        IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD
        PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR
        PUNITIVE DAMAGES, INCLUDING LOST PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES
        ARISING FROM YOUR USE OF THE SITE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
        SUCH DAMAGES. OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER WILL AT ALL TIMES BE LIMITED
        TO THE AMOUNT PAID, IF ANY, BY YOU TO US DURING THE SIX (6) MONTH PERIOD PRIOR TO ANY
        CAUSE OF ACTION ARISING.
      </p>

      <h2>Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold us harmless, including our subsidiaries,
        affiliates, and all of our respective officers, agents, partners, and employees, from
        and against any loss, damage, liability, claim, or demand, including reasonable
        attorneys' fees, made by any third party due to or arising out of: (1) use of the
        Site; (2) breach of these Terms; (3) any breach of your representations and warranties;
        (4) your violation of the rights of a third party; or (5) any overt harmful act toward
        any other user of the Site.
      </p>

      <h2>User Data</h2>
      <p>
        We will maintain certain data that you transmit to the Site for the purpose of managing
        the performance of the Site, as well as data relating to your use of the Site. Although
        we perform regular routine backups of data, you are solely responsible for all data that
        you transmit or that relates to any activity you have undertaken using the Site. You
        agree that we shall have no liability to you for any loss or corruption of any such data.
      </p>

      <h2>Electronic Communications</h2>
      <p>
        Visiting the Site, sending us emails, and completing online forms constitute electronic
        communications. You consent to receive electronic communications, and you agree that all
        agreements, notices, disclosures, and other communications we provide to you
        electronically satisfy any legal requirement that such communication be in writing.
      </p>

      <h2>Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State
        of Washington, United States, without regard to its conflict of law provisions. For
        services facilitated through Vivid Productions, Co. Ltd. in Thailand, Thai consumer
        protection laws may additionally apply.
      </p>

      <h2>Miscellaneous</h2>
      <p>
        These Terms of Service and any policies or operating rules posted by us on the Site
        constitute the entire agreement and understanding between you and us. Our failure to
        exercise or enforce any right or provision of these Terms shall not operate as a waiver
        of such right or provision. If any provision of these Terms is determined to be unlawful,
        void, or unenforceable, that provision is deemed severable and does not affect the
        validity of the remaining provisions. We may assign any or all of our rights and
        obligations to others at any time.
      </p>

      <h2>Contact Us</h2>
      <p>
        In order to resolve a complaint regarding the Site or to receive further information
        regarding use of the Site, please contact us at:
      </p>
      <p>
        <b>Vivid Productions, LLC</b><br />
        100 N Howard St Ste R<br />
        Spokane, WA 99201-0508<br />
        United States<br />
        Phone: +12345428111<br />
        Email: <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>
      </p>
      <p>
        <b>Thai services:</b><br />
        Vivid Productions, Co. Ltd.<br />
        Siripanich Building 5th Floor, Room 5I<br />
        No. 191 Moo 2, Huaykaew Rd<br />
        T. Suthep, Muang, Chiang Mai 50200<br />
        Thailand
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Privacy Policy
//
// lib/legalContent.js has a plain-HTML mirror of this content, server-
// rendered into #root for crawlers that don't run JavaScript (Google's OAuth
// verification review flagged /privacy as empty for exactly this reason).
// Update both together when this policy changes.
// ---------------------------------------------------------------------------
export function PrivacyPage({ me, currency, onCurrency }) {
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="Privacy Policy">
      <p className="legal-updated">Last updated September 22, 2026</p>

      <p>
        Thank you for choosing to be part of our community at Vivid Productions, LLC, doing
        business as MobileTopup.com ("MobileTopup.com," "we," "us," or "our"). We are
        committed to protecting your personal information and your right to privacy. If you
        have any questions or concerns about this privacy notice or our practices, please
        contact us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.
      </p>
      <p>This privacy notice describes how we might use your information if you:</p>
      <ul>
        <li>Visit our website at <a href="https://mobiletopup.com">https://mobiletopup.com</a></li>
        <li>Use the Partners Portal at partners.mobiletopup.com</li>
        <li>Interact with the Eve AI agent at agent.mobiletopup.com (via web, WhatsApp, or Telegram)</li>
        <li>Download and use our mobile application</li>
        <li>Engage with us in other related ways, including any sales, marketing, or events</li>
      </ul>
      <p>In this privacy notice, if we refer to:</p>
      <ul>
        <li><b>"Website"</b> — any website of ours that references or links to this policy</li>
        <li><b>"App"</b> — any application of ours that references or links to this policy</li>
        <li><b>"Services"</b> — our Website, App, APIs, AI agent, and other related services</li>
      </ul>

      <h2>Table of Contents</h2>
      <ol className="legal-toc">
        <li><a href="#info-collect">What Information Do We Collect?</a></li>
        <li><a href="#info-use">How Do We Use Your Information?</a></li>
        <li><a href="#info-share">Will Your Information Be Shared With Anyone?</a></li>
        <li><a href="#cookies">Do We Use Cookies and Other Tracking Technologies?</a></li>
        <li><a href="#social">How Do We Handle Your Social Logins?</a></li>
        <li><a href="#retention">How Long Do We Keep Your Information?</a></li>
        <li><a href="#security">How Do We Keep Your Information Safe?</a></li>
        <li><a href="#minors">Do We Collect Information from Minors?</a></li>
        <li><a href="#rights">What Are Your Privacy Rights?</a></li>
        <li><a href="#dnt">Controls for Do-Not-Track Features</a></li>
        <li><a href="#california">Do California Residents Have Specific Privacy Rights?</a></li>
        <li><a href="#updates">Do We Make Updates to This Notice?</a></li>
        <li><a href="#contact">How Can You Contact Us About This Notice?</a></li>
        <li><a href="#review">How Can You Review, Update, or Delete the Data We Collect?</a></li>
      </ol>

      <h2 id="info-collect">1. What Information Do We Collect?</h2>
      <h3>Personal information you disclose to us</h3>
      <p><i><b>In Short:</b> We collect personal information that you provide to us.</i></p>
      <p>
        We collect personal information that you voluntarily provide to us when you register on
        the Services, make a purchase, express an interest in obtaining information about us or
        our products, or otherwise contact us.
      </p>
      <p>The personal information we collect may include:</p>
      <ul>
        <li><b>Identity data:</b> names, email addresses, phone numbers, account credentials</li>
        <li><b>Transaction data:</b> phone numbers you send topups to, order history, product selections</li>
        <li><b>Payment data:</b> we do not store full credit card numbers. All payment data is processed
          by our payment processors. You may find their privacy notices here:
          <ul>
            <li><a href="https://stripe.com/privacy">Stripe</a></li>
            <li><a href="https://www.omise.co/privacy">Omise (Opn Payments)</a></li>
            <li><a href="https://www.paypal.com/myaccount/privacy/privacyhub">PayPal</a></li>
          </ul>
        </li>
        <li><b>Social login data:</b> if you register using Google or Facebook, we receive your name, email address, and profile picture from those providers.</li>
        <li><b>AI agent interactions:</b> if you use Eve (our AI agent via WhatsApp, Telegram, or web chat), we store conversation history to provide support and fulfill orders.</li>
      </ul>

      <h3>Information automatically collected</h3>
      <p><i><b>In Short:</b> Some information — such as your IP address and browser characteristics — is collected automatically when you visit our Services.</i></p>
      <p>
        We automatically collect certain information when you visit, use, or navigate the
        Services. This information does not reveal your specific identity but may include:
      </p>
      <ul>
        <li><b>Log and usage data:</b> IP address, browser type, pages viewed, date/time stamps, referring URLs</li>
        <li><b>Device data:</b> device type, operating system, unique device identifiers</li>
        <li><b>Location data:</b> approximate location based on your IP address, used to detect your country for service customization (e.g., display currency, relevant carriers)</li>
      </ul>

      <h2 id="info-use">2. How Do We Use Your Information?</h2>
      <p><i><b>In Short:</b> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</i></p>
      <p>We use the information we collect or receive:</p>
      <ul>
        <li><b>To fulfill orders:</b> process your topup, eSIM, and gift card purchases and deliver them to the recipient.</li>
        <li><b>To manage your account:</b> create and manage your registration, maintain your order history and saved phone numbers.</li>
        <li><b>To process payments:</b> facilitate payment through our integrated payment processors.</li>
        <li><b>To communicate with you:</b> send order confirmations, receipts, and service-related emails via Mailgun.</li>
        <li><b>To provide support:</b> respond to your inquiries via email, WhatsApp, Telegram, or our AI agent.</li>
        <li><b>To detect and prevent fraud:</b> monitor transactions for suspicious activity, enforce our Terms of Service, and protect our users.</li>
        <li><b>To improve our Services:</b> understand how our Services are used and identify areas for improvement.</li>
        <li><b>For error tracking:</b> we use Sentry to capture and diagnose software errors, which may include technical context about your session (no personal information is intentionally collected for this purpose).</li>
      </ul>

      <h2 id="info-share">3. Will Your Information Be Shared With Anyone?</h2>
      <p><i><b>In Short:</b> We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations.</i></p>
      <p>We may share your data with the following categories of third parties:</p>
      <ul>
        <li><b>Payment processors:</b> Stripe, Omise (Opn Payments), PayPal, Xendit — to process your payments.</li>
        <li><b>Topup and eSIM providers:</b> Ding, Reloadly, eSIM Access, Wondd — to deliver your purchased services. We share only the phone number or account identifier necessary to fulfill the order.</li>
        <li><b>Email service:</b> Mailgun — to send transactional emails (receipts, confirmations).</li>
        <li><b>Error tracking:</b> Sentry — to monitor and fix software errors.</li>
        <li><b>Analytics:</b> Google Analytics — to understand site usage patterns.</li>
        <li><b>Reviews:</b> Trustpilot — to collect and display customer reviews.</li>
        <li><b>Carrier lookup:</b> Twilio — to identify mobile carriers for phone numbers you submit.</li>
        <li><b>Bot protection:</b> Cloudflare Turnstile — to prevent automated abuse.</li>
        <li><b>Hosting:</b> DigitalOcean (main app), Vercel (Partners Portal and Eve agent), Neon (database).</li>
      </ul>
      <p>
        We do not sell your personal information. Vivid Productions, LLC has not sold any
        personal information to third parties for a business or commercial purpose.
      </p>

      <h2 id="cookies">4. Do We Use Cookies and Other Tracking Technologies?</h2>
      <p><i><b>In Short:</b> We use cookies and similar technologies to collect and store information.</i></p>
      <p>
        We use cookies and similar tracking technologies to access or store information.
        For detailed information about the cookies we use, please see our{' '}
        <a href="/cookie-privacy">Cookie Policy</a>.
      </p>

      <h2 id="social">5. How Do We Handle Your Social Logins?</h2>
      <p><i><b>In Short:</b> If you choose to register or log in using a social media account, we may have access to certain information about you.</i></p>
      <p>
        Our Services offer you the ability to register and log in using your Google or Facebook
        account. When you choose to do this, we will receive your name, email address, and
        profile picture from the social media provider. We use this information only to create
        and manage your account. We do not post to your social media accounts or access your
        contacts, friends lists, or other data beyond what is listed above.
      </p>
      <h3>Google API Services User Data</h3>
      <p>
        Our use and transfer of information received from Google APIs to any other app adheres
        to the <a href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</a>,
        including the Limited Use requirements. Specifically:
      </p>
      <ul>
        <li><b>What we access:</b> only your name, email address, and profile picture, requested at sign-in.</li>
        <li><b>How we use it:</b> solely to create and manage your MobileTopup.com account, verify your identity, and communicate with you about your account and orders.</li>
        <li><b>Sharing:</b> we do not share, transfer, or disclose your Google account data to any third party, except as described in "Will Your Information Be Shared With Anyone?" above (for example, our email provider Mailgun, to send you order-related messages) — and never for advertising.</li>
        <li><b>Protection:</b> your Google account data is protected using the same technical and organizational security measures described in "How Do We Keep Your Information Safe?" below.</li>
        <li><b>Retention and deletion:</b> your Google account data is retained only for as long as your account remains active and is deleted along with your account data upon account termination, as described in "How Long Do We Keep Your Information?" and "What Are Your Privacy Rights?" below.</li>
      </ul>
      <p>
        We do not use Google user data to serve advertisements, sell it to data brokers or any
        other third party, use it to determine creditworthiness or for lending purposes, or use
        it to train generalized or non-personalized AI or machine learning models.
      </p>

      <h2 id="retention">6. How Long Do We Keep Your Information?</h2>
      <p><i><b>In Short:</b> We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.</i></p>
      <p>
        We will only keep your personal information for as long as it is necessary for the
        purposes set out in this privacy notice, unless a longer retention period is required
        or permitted by law (such as tax, accounting, or other legal requirements). When we
        have no ongoing legitimate business need to process your personal information, we will
        either delete or anonymize such information.
      </p>

      <h2 id="security">7. How Do We Keep Your Information Safe?</h2>
      <p><i><b>In Short:</b> We aim to protect your personal information through a system of organizational and technical security measures.</i></p>
      <p>
        We have implemented appropriate technical and organizational security measures designed
        to protect the security of any personal information we process. These include encrypted
        connections (TLS/HTTPS), hashed API keys (SHA-256), secure payment processing through
        PCI-compliant providers, and containerized infrastructure. However, no electronic
        transmission over the Internet can be guaranteed to be 100% secure.
      </p>

      <h2 id="minors">8. Do We Collect Information from Minors?</h2>
      <p><i><b>In Short:</b> We do not knowingly collect data from or market to children under 18 years of age.</i></p>
      <p>
        We do not knowingly solicit data from or market to children under 18 years of age.
        By using the Services, you represent that you are at least 18 or that you are the
        parent or guardian of such a minor. If we learn that personal information from users
        less than 18 years of age has been collected, we will deactivate the account and take
        reasonable measures to promptly delete such data. If you become aware of any data we
        may have collected from children under age 18, please contact us at{' '}
        <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.
      </p>

      <h2 id="rights">9. What Are Your Privacy Rights?</h2>
      <p><i><b>In Short:</b> Depending on your location, you may have rights that allow you greater access to and control over your personal information.</i></p>
      <p>
        In some regions (like the EEA, UK, and certain US states), you have rights under
        applicable data protection laws. These may include the right to request access to and
        obtain a copy of your personal information, to request rectification or erasure, to
        restrict processing, and to data portability. To exercise these rights, please contact
        us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.
      </p>
      <h3>Account Information</h3>
      <p>If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
      <ul>
        <li>Log in to your <a href="/settings">account settings</a> and update your information.</li>
        <li>Contact us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.</li>
      </ul>
      <p>
        Upon your request to terminate your account, we will deactivate or delete your account
        and information from our active databases. We may retain some information to prevent
        fraud, troubleshoot problems, assist with investigations, enforce our Terms of Service,
        and/or comply with applicable legal requirements.
      </p>
      <h3>Opting Out of Email Marketing</h3>
      <p>
        You can unsubscribe from our marketing email list at any time by clicking the
        unsubscribe link in the emails that we send. We may still send you service-related
        emails necessary for account administration (e.g., order confirmations, receipts).
      </p>

      <h2 id="dnt">10. Controls for Do-Not-Track Features</h2>
      <p>
        Most web browsers include a Do-Not-Track ("DNT") feature or setting. At this time, no
        uniform technology standard for recognizing and implementing DNT signals has been
        finalized. As such, we do not currently respond to DNT browser signals. If a standard
        for online tracking is adopted that we must follow in the future, we will inform you
        about that practice in a revised version of this privacy notice.
      </p>

      <h2 id="california">11. Do California Residents Have Specific Privacy Rights?</h2>
      <p><i><b>In Short:</b> Yes, if you are a resident of California, you are granted specific rights regarding access to your personal information.</i></p>
      <p>
        California Civil Code Section 1798.83, also known as the "Shine The Light" law,
        permits California residents to request information about categories of personal
        information we disclosed to third parties for direct marketing purposes. We do not
        disclose personal information to third parties for direct marketing purposes.
      </p>
      <p>
        Under the California Consumer Privacy Act (CCPA), California residents have the right
        to request deletion of their data, the right to know what data we collect and how it
        is used, and the right to non-discrimination for exercising their privacy rights. To
        exercise these rights, contact us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.
      </p>

      <h2 id="updates">12. Do We Make Updates to This Notice?</h2>
      <p><i><b>In Short:</b> Yes, we will update this notice as necessary to stay compliant with relevant laws.</i></p>
      <p>
        We may update this privacy notice from time to time. The updated version will be
        indicated by an updated "Last updated" date. We encourage you to review this privacy
        notice frequently to be informed of how we are protecting your information.
      </p>

      <h2 id="contact">13. How Can You Contact Us About This Notice?</h2>
      <p>If you have questions or comments about this notice, you may email us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a> or by post to:</p>
      <p>
        <b>Vivid Productions, LLC</b><br />
        100 N Howard St Ste R<br />
        Spokane, WA 99201-0508<br />
        United States
      </p>
      <p>
        <b>Thai services:</b><br />
        Vivid Productions, Co. Ltd.<br />
        Siripanich Building 5th Floor, Room 5I<br />
        No. 191 Moo 2, Huaykaew Rd<br />
        T. Suthep, Muang, Chiang Mai 50200<br />
        Thailand
      </p>

      <h2 id="review">14. How Can You Review, Update, or Delete the Data We Collect?</h2>
      <p>
        Based on the applicable laws of your country, you may have the right to request access
        to the personal information we collect from you, change that information, or delete it.
        To request to review, update, or delete your personal information, please email us at{' '}
        <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>.
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Cookie Policy
// ---------------------------------------------------------------------------
export function CookiePolicyPage({ me, currency, onCurrency }) {
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="Cookie Policy">
      <p className="legal-updated">Last updated August 21, 2026</p>

      <p>
        This Cookie Policy explains how Vivid Productions, LLC (<b>"Company"</b>, <b>"we"</b>,{' '}
        <b>"us"</b>, and <b>"our"</b>) uses cookies and similar technologies to recognize you when
        you visit our website at <a href="https://mobiletopup.com">https://mobiletopup.com</a> (<b>"Website"</b>).
        It explains what these technologies are, why we use them, and your rights to control our
        use of them.
      </p>

      <h2>What Are Cookies?</h2>
      <p>
        Cookies are small data files that are placed on your computer or mobile device when you
        visit a website. Cookies set by the website owner (in this case, Vivid Productions, LLC)
        are called "first party cookies." Cookies set by parties other than the website owner are
        called "third party cookies."
      </p>

      <h2>Why Do We Use Cookies?</h2>
      <p>
        We use first and third party cookies for several reasons. Some cookies are required for
        technical reasons in order for our Website to operate (essential cookies). Other cookies
        enable us to track and target the interests of our users to enhance the experience.
        Third parties serve cookies through our Website for analytics and other purposes.
      </p>

      <h2>Cookies We Use</h2>

      <h3>Essential Cookies</h3>
      <p>These cookies are strictly necessary for the Website to function and cannot be switched off.</p>
      <table className="legal-table">
        <thead>
          <tr><th>Cookie</th><th>Provider</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td><code>connect.sid</code></td><td>mobiletopup.com</td><td>Session identifier — keeps you logged in</td><td>Session</td></tr>
          <tr><td><code>lang</code></td><td>mobiletopup.com</td><td>Remembers your language preference</td><td>~3 years</td></tr>
          <tr><td><code>mt_locale</code></td><td>mobiletopup.com</td><td>Stores language and display currency preference</td><td>1 year</td></tr>
          <tr><td><code>cf_clearance</code></td><td>Cloudflare</td><td>Cloudflare Turnstile bot protection — proves you passed the challenge</td><td>30 min</td></tr>
        </tbody>
      </table>

      <h3>Functional Cookies</h3>
      <p>These cookies enable enhanced functionality and personalization.</p>
      <table className="legal-table">
        <thead>
          <tr><th>Cookie / Storage</th><th>Provider</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td><code>mt_bfp</code></td><td>mobiletopup.com</td><td>Browser fingerprint used for fraud prevention</td><td>Persistent (localStorage)</td></tr>
          <tr><td><code>mt_currency</code></td><td>mobiletopup.com</td><td>Display currency preference (THB, USD, PHP, EUR, GBP)</td><td>Persistent (localStorage)</td></tr>
          <tr><td><code>mt_country_choice</code></td><td>mobiletopup.com</td><td>The country you chose as your default location in Preferences — used to open that country's home page</td><td>1 year (cookie)</td></tr>
          <tr><td><code>mt_country_pref</code></td><td>mobiletopup.com</td><td>Last country selected — keeps catalog pages consistent</td><td>Persistent (localStorage)</td></tr>
          <tr><td><code>mt_recent_phones</code></td><td>mobiletopup.com</td><td>Recently used phone numbers for autocomplete (stored locally only)</td><td>Persistent (localStorage)</td></tr>
          <tr><td><code>G_ENABLED_IDPS</code></td><td>Google</td><td>Google Sign-In — tracks enabled identity providers</td><td>Long-lived</td></tr>
        </tbody>
      </table>

      <h3>Analytics Cookies</h3>
      <p>These cookies help us understand how visitors interact with our Website.</p>
      <table className="legal-table">
        <thead>
          <tr><th>Cookie</th><th>Provider</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td><code>_ga</code></td><td>Google Analytics</td><td>Distinguishes unique users</td><td>2 years</td></tr>
          <tr><td><code>_ga_*</code></td><td>Google Analytics</td><td>Maintains session state</td><td>2 years</td></tr>
          <tr><td><code>_gid</code></td><td>Google Analytics</td><td>Distinguishes users (short-lived)</td><td>24 hours</td></tr>
          <tr><td><code>_gat</code></td><td>Google Analytics</td><td>Throttles request rate</td><td>1 minute</td></tr>
          <tr><td><code>ajs_anonymous_id</code></td><td>Trustpilot</td><td>Counts unique visitors</td><td>1 year</td></tr>
          <tr><td><code>ajs_user_id</code></td><td>Trustpilot</td><td>Tracks visitor usage and events</td><td>Persistent (localStorage)</td></tr>
          <tr><td><code>ajs_group_id</code></td><td>Trustpilot</td><td>Tracks visitor group membership</td><td>Persistent (localStorage)</td></tr>
        </tbody>
      </table>

      <h3>Advertising Cookies</h3>
      <p>These cookies are used to deliver relevant advertisements and measure their effectiveness.</p>
      <table className="legal-table">
        <thead>
          <tr><th>Cookie</th><th>Provider</th><th>Purpose</th><th>Expiry</th></tr>
        </thead>
        <tbody>
          <tr><td><code>_gcl_au</code></td><td>Google AdSense</td><td>Stores and tracks conversions from ad clicks</td><td>90 days</td></tr>
          <tr><td><code>IDE</code></td><td>DoubleClick (Google)</td><td>Measures ad conversion rates</td><td>2 years</td></tr>
          <tr><td><code>test_cookie</code></td><td>DoubleClick (Google)</td><td>Checks if the browser supports cookies</td><td>15 minutes</td></tr>
          <tr><td><code>NID</code></td><td>Google</td><td>Stores user preferences and information for Google maps and ads</td><td>6 months</td></tr>
        </tbody>
      </table>

      <h2>How Can I Control Cookies?</h2>
      <p>
        You have the right to decide whether to accept or reject cookies. You can set your
        web browser to accept or refuse cookies. Most browsers accept cookies by default. If
        you choose to reject cookies, you may still use our Website though some functionality
        may be limited.
      </p>
      <p>
        To opt out of interest-based advertising, visit{' '}
        <a href="http://www.aboutads.info/choices/" rel="noopener noreferrer" target="_blank">www.aboutads.info/choices</a> or{' '}
        <a href="http://www.youronlinechoices.com" rel="noopener noreferrer" target="_blank">www.youronlinechoices.com</a>.
      </p>

      <h2>Other Tracking Technologies</h2>
      <p>
        Cookies are not the only way to recognize or track visitors to a website. We may use
        web beacons (sometimes called "tracking pixels") to monitor traffic patterns and measure
        the success of email marketing campaigns. These technologies are often reliant on cookies
        to function properly, so declining cookies will impair their functioning.
      </p>

      <h2>How Often Will You Update This Cookie Policy?</h2>
      <p>
        We may update this Cookie Policy from time to time to reflect changes to the cookies
        we use or for other operational, legal, or regulatory reasons. The date at the top
        indicates when it was last updated.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions about our use of cookies or other technologies, please email
        us at <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a> or by post to:
      </p>
      <p>
        <b>Vivid Productions, LLC</b><br />
        100 N Howard St Ste R<br />
        Spokane, WA 99201-0508<br />
        United States<br />
        Phone: +12345428111
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Vulnerability Disclosure Policy
//
// lib/legalContent.js has a plain-HTML mirror of this content, server-
// rendered into #root for crawlers that don't run JavaScript. Update both
// together when this policy changes. /.well-known/security.txt (RFC 9116)
// points here via its Policy field.
// ---------------------------------------------------------------------------
export function SecurityPage({ me, currency, onCurrency }) {
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="Vulnerability Disclosure Policy">
      <p className="legal-updated">Last updated September 25, 2026</p>

      <p>
        Vivid Productions, LLC, doing business as MobileTopup.com, values the work of independent
        security researchers in keeping our users safe. This page explains how to report a
        suspected security vulnerability in our systems and what to expect from us in response.
      </p>

      <h2>Scope</h2>
      <p>The following are in scope for vulnerability reports:</p>
      <ul>
        <li><a href="https://mobiletopup.com">mobiletopup.com</a> (the main website and web app)</li>
        <li>The admin panel at mobiletopup.com/admin — report-only; please do not attempt to log in
          or actively test this panel</li>
        <li><a href="https://partners.mobiletopup.com">partners.mobiletopup.com</a>, including the
          Partner API v1</li>
        <li><a href="https://agent.mobiletopup.com">agent.mobiletopup.com</a> (the Eve AI agent)</li>
        <li>Our iOS and Android mobile applications</li>
      </ul>
      <p>
        <b>Out of scope:</b> dev.mobiletopup.com and any other staging/development subdomain are
        explicitly out of scope — they run against live production data and must not be targeted.
        Also out of scope: denial-of-service testing, spam or social engineering against our staff
        or users, physical security testing, and automated vulnerability scanning that generates
        significant traffic without prior coordination with us.
      </p>

      <h2>How to Report</h2>
      <p>
        Please email <a href="mailto:security@mobiletopup.com">security@mobiletopup.com</a> with
        one issue per email. To help us triage and reproduce your report quickly, please include:
      </p>
      <ul>
        <li>A description of the vulnerability and its potential impact</li>
        <li>Step-by-step reproduction instructions or a proof of concept</li>
        <li>The URL, endpoint, or app version affected</li>
        <li>Any tools or scripts used, so we can distinguish your testing traffic from an attack</li>
      </ul>
      <p>Please do not include real customer data, payment card numbers, or account passwords in your report.</p>

      <h2>Our Response</h2>
      <p>
        We will acknowledge receipt of your report within 3 business days. We will keep you
        informed of our progress as we investigate and remediate confirmed issues, and we will
        let you know once a fix has been deployed.
      </p>

      <h2>Safe Harbor</h2>
      <p>
        We will not pursue legal action against researchers who make a good-faith effort to
        comply with this policy: testing only in-scope systems, avoiding harm to our users and
        services, not accessing or exfiltrating more data than necessary to demonstrate an issue,
        and reporting findings to us promptly and privately before any public disclosure.
      </p>

      <h2>Recognition</h2>
      <p>
        We do not currently offer a paid bug bounty program. We are happy to credit researchers
        who report valid, previously-unknown vulnerabilities, with their permission, once a fix
        has shipped.
      </p>

      <h2>Contact</h2>
      <p>
        Email <a href="mailto:security@mobiletopup.com">security@mobiletopup.com</a>. A
        machine-readable copy of this contact information is published at{' '}
        <a href="/.well-known/security.txt">/.well-known/security.txt</a> per RFC 9116.
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Refund Policy
//
// lib/legalContent.js has a plain-HTML mirror of this content, server-
// rendered into #root for crawlers that don't run JavaScript. Update both
// together when this policy changes.
// ---------------------------------------------------------------------------
export function RefundPolicyPage({ me, currency, onCurrency }) {
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="Refund Policy">
      <p className="legal-updated">Last updated October 2, 2026</p>

      <div style={{ background: '#f0f7f0', border: '1px solid #c3dbc3', borderRadius: 8, padding: '16px 20px', marginBottom: 24 }}>
        <b>Summary:</b>
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          <li>Failed orders are refunded automatically — usually within 15 minutes</li>
          <li>Successfully delivered top-ups, codes and PINs are final</li>
          <li>Unused eSIMs (zero data consumed) get a full refund</li>
        </ul>
      </div>

      <h2>International Airtime Top-ups</h2>
      <p>
        International top-ups are delivered via Ding, Reloadly, or Zendit depending on the
        destination country and carrier.
      </p>
      <ul>
        <li><b>Successful delivery:</b> the sale is final. Once credit has been added to the
          recipient's phone, we cannot reverse it.</li>
        <li><b>Failed delivery:</b> if the top-up fails at the provider, a refund is issued
          automatically. Our system checks for failed orders every 15 minutes. Most refunds
          complete within 30 minutes of the original order.</li>
        <li><b>Wrong phone number:</b> if you enter an incorrect phone number and the top-up is
          delivered to the wrong recipient, we cannot issue a refund. Please double-check the
          number before placing your order.</li>
      </ul>

      <h2>Thailand Top-ups (AIS, DTAC, TrueMove H)</h2>
      <p>
        Thai carrier top-ups are processed through Vivid Productions, Co. Ltd. and its
        authorized telecom partners.
      </p>
      <ul>
        <li><b>Successful delivery:</b> the sale is final.</li>
        <li><b>Failed delivery:</b> refunded automatically. The carrier mismatch detection
          system will attempt to re-send to the correct carrier before failing an order.</li>
        <li><b>Operator delays:</b> some carrier-side delays can occur. Our system monitors
          pending orders for up to 24 hours before timing out and issuing a refund.</li>
      </ul>

      <h2>eSIM Data Plans</h2>
      <ul>
        <li><b>Unused eSIM (zero data consumed):</b> you receive a <b>full refund</b>. You can
          request a refund from your <a href="/orders">My eSIMs</a> page — if the plan shows
          zero usage, the refund is processed instantly.</li>
        <li><b>eSIM with data used:</b> once any data has been consumed, no refund is available.</li>
        <li><b>Failed purchase:</b> if an eSIM plan fails to provision (e.g. the supplier is
          out of stock), you are refunded automatically.</li>
        <li><b>eSIM top-ups:</b> follow the same rules — if the top-up fails, you are refunded;
          once data is consumed, the sale is final.</li>
      </ul>

      <h2>Gift Cards, Game Credit &amp; Utility Payments</h2>
      <ul>
        <li><b>Code delivered successfully:</b> all gift card and voucher codes are <b>final</b> once
          delivered. Codes cannot be returned, exchanged, or refunded after delivery.</li>
        <li><b>Failed delivery or invalid code:</b> if a code fails to deliver or is reported as
          invalid within 48 hours of purchase, contact us for a refund. Please include your
          order ID.</li>
      </ul>

      <h2>Data Bundles &amp; PINs</h2>
      <ul>
        <li><b>PIN/voucher delivered:</b> the sale is final once the PIN or voucher code is revealed.</li>
        <li><b>Failed delivery:</b> refunded automatically.</li>
      </ul>

      <h2 id="refund-destinations">Where Refunds Go</h2>
      <p>
        The refund destination depends on how you paid:
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
            <th style={{ padding: '8px 12px' }}>Payment Method</th>
            <th style={{ padding: '8px 12px' }}>Refund Destination</th>
            <th style={{ padding: '8px 12px' }}>Expected Timing</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>Credit/debit card (Omise)</td>
            <td style={{ padding: '8px 12px' }}>Original card</td>
            <td style={{ padding: '8px 12px' }}>3–10 business days</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>Credit/debit card (Stripe)</td>
            <td style={{ padding: '8px 12px' }}>Original card</td>
            <td style={{ padding: '8px 12px' }}>5–10 business days</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>PayPal</td>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Within 30 minutes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>Alipay</td>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Within 30 minutes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>PromptPay (via Omise)</td>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Within 30 minutes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>Xendit (Maya, ShopeePay, BPI, etc.)</td>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Within 30 minutes</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Account balance</td>
            <td style={{ padding: '8px 12px' }}>Instant</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 12px' }}>USDC (Partners Portal)</td>
            <td style={{ padding: '8px 12px' }}>USD wallet balance</td>
            <td style={{ padding: '8px 12px' }}>Instant</td>
          </tr>
        </tbody>
      </table>
      <p>
        "Account balance" means your MobileTopup.com wallet — you can use it for future
        purchases immediately. Card refunds depend on your bank's processing time.
      </p>

      <h2>Edge Cases</h2>
      <ul>
        <li><b>Wrong phone number:</b> if you entered an incorrect number and the top-up was
          delivered, we cannot reverse it. Always double-check the number before paying.</li>
        <li><b>Duplicate payments:</b> if you are charged twice for the same order, contact us
          and the duplicate charge will be refunded to the original payment method.</li>
        <li><b>Operator-side delays:</b> some mobile operators take longer to process top-ups.
          Our system monitors pending orders for up to 24 hours. If the top-up still hasn't
          been confirmed after that time, the order is failed and refunded automatically.</li>
      </ul>

      <h2>How to Request a Refund</h2>
      <ul>
        <li><b>eSIMs:</b> use the self-service refund button on your <a href="/orders">My eSIMs</a> page.</li>
        <li><b>All other products:</b> contact us at{' '}
          <a href="/contact">mobiletopup.com/contact</a> or email{' '}
          <a href="mailto:info@mobiletopup.com">info@mobiletopup.com</a>. Include your
          order ID (found in your confirmation email or <a href="/orders">order history</a>).</li>
        <li>You can also reach us via <a href="/support">WhatsApp, Telegram, or our AI agent Eve</a>.</li>
      </ul>

      <h2>Policy Changes</h2>
      <p>
        We may update this refund policy from time to time. The "Last updated" date at the top
        will reflect any changes. Your continued use of the Site constitutes acceptance of the
        updated policy.
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------
// Bubbling "+" particles around and inside the logo mark. Sizes, delays,
// speeds and peak opacities all differ so the pattern never visibly repeats.
const ABOUT_CREDITS = [
  // left of the icon
  { x: 38, y: 150, size: 18, delay: '.8s', dur: '4.8s', peak: '.5' },
  { x: 50, y: 185, size: 13, delay: '2.6s', dur: '4.2s', peak: '.35' },
  { x: 30, y: 205, size: 15, delay: '4.4s', dur: '5.2s', peak: '.4' },
  // inside the screen
  { x: 95, y: 168, size: 20, delay: '0s', dur: '4.5s', peak: '.85' },
  { x: 112, y: 176, size: 16, delay: '1.5s', dur: '4.5s', peak: '.7' },
  { x: 128, y: 170, size: 19, delay: '3s', dur: '4.5s', peak: '.85' },
  // right of the icon
  { x: 184, y: 160, size: 16, delay: '1.9s', dur: '4.6s', peak: '.45' },
  { x: 196, y: 195, size: 13, delay: '3.7s', dur: '4.1s', peak: '.35' },
  { x: 176, y: 210, size: 18, delay: '5.3s', dur: '5s', peak: '.5' },
];

export function AboutPage({ me, currency, onCurrency }) {
  const statsRef = React.useRef(null);

  // Count-up on the stat numbers. Static values are already in the markup,
  // so crawlers and reduced-motion users see the real numbers.
  React.useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!statsRef.current) return;
    statsRef.current.querySelectorAll('[data-count]').forEach((el, i) => {
      const target = +el.dataset.count;
      const dur = 1200 + i * 200;
      let t0 = null;
      const tick = (t) => {
        if (!t0) t0 = t;
        let p = Math.min((t - t0) / dur, 1);
        p = 1 - Math.pow(1 - p, 3); // ease-out cubic
        el.textContent = Math.round(target * p);
        if (p < 1) requestAnimationFrame(tick);
      };
      setTimeout(() => requestAnimationFrame(tick), 300 + i * 120);
    });
  }, []);

  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="About Us" plain>
      <div className="about-hero">
        <p className="about-lede">
          MobileTopup.com is an online platform for international prepaid mobile top-ups, travel
          eSIMs, and digital gift cards. We deliver credit to phones in more than 140 countries —
          usually in under a minute.
        </p>

        <div className="about-art" aria-hidden="true">
          <svg viewBox="0 0 220 220">
            {/* "+" bubbles rising and fading around and inside the mark */}
            <g fontFamily="proxima-nova, Helvetica, Arial, sans-serif" fontWeight="700" textAnchor="middle">
              {ABOUT_CREDITS.map((c, i) => (
                <text key={i} className="about-credit" x={c.x} y={c.y} fontSize={c.size}
                      style={{ '--delay': c.delay, '--dur': c.dur, '--peak': c.peak }}>+</text>
              ))}
            </g>
            {/* phone outline, open at the top-right where the plus sits */}
            <path className="about-mark-phone" pathLength="100"
                  d="M113 55 H88 Q68 55 68 75 V165 Q68 185 88 185 H132 Q152 185 152 165 V96" />
            {/* the plus */}
            <g className="about-mark-plus">
              <line x1="134" y1="57" x2="170" y2="57" />
              <line x1="152" y1="39" x2="152" y2="75" />
            </g>
          </svg>
        </div>
      </div>

      <div className="about-stats" ref={statsRef}>
        <div className="about-stat">
          <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
          <div className="n">2009</div><div className="l">in business since</div>
        </div>
        <div className="about-stat">
          <svg viewBox="0 0 24 24"><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>
          <div className="n"><span data-count="5">5</span>M<sup>+</sup></div><div className="l">top-ups processed</div>
        </div>
        <div className="about-stat">
          <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.5 3 14 0 18M12 3c-3 3.5-3 14 0 18" /></svg>
          <div className="n"><span data-count="140">140</span><sup>+</sup></div><div className="l">countries covered</div>
        </div>
        <div className="about-stat">
          <svg viewBox="0 0 24 24"><path d="M4 18v-6M9 18V9M14 18V5M19 18v-9" /></svg>
          <div className="n"><span data-count="500">500</span><sup>+</sup></div><div className="l">carriers supported</div>
        </div>
      </div>

      <h2>Company facts</h2>
      <table className="about-facts">
        <tbody>
          <tr><th>Founded</th><td>2009, Bangkok, Thailand — as Thaiprepaidcard.com</td></tr>
          <tr><th>U.S. company</th><td>Vivid Productions, LLC — Spokane, Washington, incorporated 2021</td></tr>
          <tr><th>Thai company</th><td>Vivid Productions Co., Ltd. — operating since 2009</td></tr>
          <tr><th>Carrier partnership</th><td>Master reseller agreement with AIS, Thailand's largest mobile network</td></tr>
          <tr><th>Coverage</th><td>140+ countries, 500+ prepaid carriers, 5 million+ top-ups processed</td></tr>
          <tr><th>Products</th><td>International airtime top-up, Thailand top-up (AIS / DTAC / TrueMove), travel eSIMs, digital gift cards, wholesale Partner API</td></tr>
          <tr><th>Payments</th><td>Credit / debit cards, Alipay, Thai banking, USDC</td></tr>
        </tbody>
      </table>

      <h2>Our history</h2>
      <p>
        MobileTopup began in Bangkok in 2009 as Thaiprepaidcard.com, the first service for topping
        up Thai prepaid phones from overseas. It grew into a wholesale distributor with direct
        carrier APIs, launched internationally as MobileTopup.com, and incorporated in the U.S. in
        2021. Today the platform covers top-ups to 140+ countries, travel eSIMs, gift cards, and a
        wholesale Partner API — with Eve, our AI agent, serving customers 24/7.
      </p>

      <h2>Why choose MobileTopup?</h2>
      <ul>
        <li><b>Instant delivery</b></li>
        <li><b>Direct carrier relationships</b></li>
        <li><b>Secure payments</b></li>
        <li><b>Automatic refunds</b></li>
        <li><b>17 years running</b></li>
      </ul>

      <h2>Our mission</h2>
      <p>
        Staying connected should be easy, affordable, and instant — wherever you are and wherever
        the phone you're topping up happens to be. We're leaning fully into AI, with one goal: to
        be exceptional at <b>customer service</b>, <b>top-up speed</b>, and <b>price</b>.
      </p>

      <h2>Contact</h2>
      <p>
        <a href="/contact">Contact us</a> · <a href="/terms">Terms of Service</a> ·{' '}
        <a href="/privacy">Privacy Policy</a>
      </p>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// AI Agents
//
// Front door for AI assistants (GitLab #119). Signed-in customers generate
// an account-bound agent key (/api/agent-keys/me, shown once) and copy one
// block of instructions into their assistant. SKILL.md and llms.txt are
// served by lib/agentFiles.js from the public agent-files repo.
// ---------------------------------------------------------------------------

// The text a customer pastes into their AI assistant. One block, key
// included, so there is nothing else to configure by hand.
function agentInstructions(key, email) {
  return [
    'Connect to the MobileTopup.com MCP server so you can help me buy mobile top-ups, travel eSIMs and gift cards.',
    '',
    `MCP server (Streamable HTTP): https://mcp.mobiletopup.com/k/${key}/mcp`,
    `If you support headers instead: https://mcp.mobiletopup.com/mcp with "Authorization: Bearer ${key}"`,
    'Guide: https://mobiletopup.com/agents/SKILL.md',
    '',
    'Rules:',
    '- Use lookup_carrier, search_products and get_product to find the product and price.',
    `- Confirm the product, amount and recipient number with me, then call create_checkout_link with my email${email ? ` (${email})` : ''}.`,
    '- Give me the checkout link. I pay on mobiletopup.com myself — never say a purchase is complete.',
    '- Keep this key private.',
  ].join('\n');
}

function AgentKeyPanel({ me }) {
  const [status, setStatus] = React.useState(null);   // { hasKey, keyPrefix, createdAt, lastUsedAt }
  const [key, setKey] = React.useState(null);         // shown once, after generating
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/agent-keys/me', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok ? r.json() : { hasKey: false }))
      .then(setStatus)
      .catch(() => setStatus({ hasKey: false }));
  }, []);

  async function generate() {
    if (status && status.hasKey && !window.confirm('This replaces your current key. Any agent using it will stop working. Continue?')) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/agent-keys/me', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.key) throw new Error((data.error && data.error.message) || 'Could not create a key. Please try again.');
      setKey(data.key);
      setStatus({ hasKey: true, keyPrefix: data.key.slice(0, 8), createdAt: new Date().toISOString() });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    navigator.clipboard.writeText(agentInstructions(key, me.email)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function disconnect() {
    if (!window.confirm('Disconnect all assistants signed in with your MobileTopup account?')) return;
    await fetch('/api/agent-keys/me/disconnect', { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    setStatus((st) => ({ ...st, connections: [] }));
  }

  const connected = status && status.connections && status.connections.length ? (
    <div className="ag-panel">
      <ul>
        {status.connections.map((c) => (
          <li key={c.clientId}><b>{c.name}</b> is connected — last used {new Date(c.lastUsedAt).toLocaleString()}</li>
        ))}
      </ul>
      <button type="button" className="ag-btn" onClick={disconnect}>Disconnect</button>
    </div>
  ) : null;

  if (!status) return <p>Loading…</p>;

  if (key) {
    return (
      <>{connected}<div className="ag-panel">
        <ul>
          <li><b>Copy this into your AI assistant</b> (Claude, ChatGPT, …)</li>
          <li>Your key is shown only once — copy it now</li>
        </ul>
        <pre className="ag-pre">
          {agentInstructions(key, me.email)}
        </pre>
        <button type="button" className="ag-btn primary" onClick={copy}>{copied ? 'Copied ✓' : 'Copy instructions'}</button>
      </div></>
    );
  }

  return (
    <>{connected}<div className="ag-panel">
      {status.hasKey ? (
        <ul>
          <li>Your agent key: <code>{status.keyPrefix}…</code></li>
          {status.lastUsedAt && <li>Last used {new Date(status.lastUsedAt).toLocaleString()}</li>}
          <li>Keys are shown only once. Generate a new one to copy fresh instructions — it replaces the old key.</li>
        </ul>
      ) : (
        <ul>
          <li>Generate your free agent key</li>
          <li>You get one block of instructions to paste into your AI assistant</li>
        </ul>
      )}
      {error && <div className="notice payerr">{error}</div>}
      <button type="button" className="ag-btn primary" onClick={generate} disabled={busy}>
        {busy ? 'Creating…' : status.hasKey ? 'Generate new key' : 'Generate key'}
      </button>
    </div></>
  );
}

// Illustration: a sample assistant conversation, modelled on a real test
// (AIS ฿30 top-up). Built as HTML, not an image, so it stays sharp and
// readable by crawlers. Tool calls are shown as small chips.
const AGENT_DEMO = [
  { who: 'user', text: 'Top up my mum’s phone in Thailand with 300 baht — +66 81 234 5678' },
  { who: 'tool', text: 'lookup_carrier → AIS, Thailand' },
  { who: 'tool', text: 'get_product → AIS ฿300' },
  { who: 'agent', text: 'That’s an AIS number. ฿300 AIS top-up:', bullets: ['Card or Alipay: $10', 'Thai bank apps: $10', 'USDC: $9.50'], after: 'Send the checkout link to you@example.com?' },
  { who: 'user', text: 'Yes please' },
  { who: 'tool', text: 'create_checkout_link' },
  { who: 'agent', text: 'Here’s your checkout — open it, check the details and pay. Nothing is charged until you do.', link: 'mobiletopup.com/a/…' },
];



// Small UI glyphs (stroke icons) for the safeguards row and the Claude card.
const Glyph = ({ d }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="ag-glyph"><path d={d} /></svg>
);
const GLYPH = {
  shield: 'M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3zM9 12l2 2 4-4',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  lock: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  chevron: 'M9 6l6 6-6 6',
};

function AgentDemo() {
  return (
    <div className="agdemo" role="img" aria-label="Example conversation: an AI assistant finds an AIS top-up, shows prices and sends a checkout link">
      {AGENT_DEMO.map((m, i) => (
        <div key={i} className={'row ' + m.who}>
          {m.who === 'tool' ? <span className="chip">{m.text}</span> : (
            <div className="b">
              {m.text}
              {m.bullets && <ul>{m.bullets.map((x) => <li key={x}>{x}</li>)}</ul>}
              {m.after && <div>{m.after}</div>}
              {m.link && <div><span className="paylink">Pay on MobileTopup → {m.link}</span></div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CopyLine({ text }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="ag-cmd">
      <span>{text}</span>
      <button type="button" onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}>
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );
}

export function AgentsPage({ me, currency, onCurrency }) {
  const channelSub = { WhatsApp: 'Chat on WhatsApp', Telegram: 'Chat on Telegram', Messenger: 'Chat on Messenger' };
  return (
    <LegalShell me={me} currency={currency} onCurrency={onCurrency} title="AI Agents" plain>
      <div className="ag">
        <section className="ag-card">
          <h2>Chat with Eve, our AI assistant</h2>
          <ul>
            <li>Top-ups, eSIMs and gift cards for 140+ countries</li>
            <li>Any language, 24/7</li>
            <li>Order help and support in the same chat</li>
          </ul>
          <div className="ag-channels">
            {SUPPORT_CHANNELS.map((c) => (
              <a key={c.name} className="ag-channel" href={c.href} target="_blank" rel="noopener noreferrer">
                <span className="ag-channel-icon">{c.icon}</span>
                <span className="ag-channel-text"><b>{c.name}</b><small>{channelSub[c.name] || 'Chat with Eve'}</small></span>
                <Glyph d={GLYPH.chevron} />
              </a>
            ))}
          </div>
        </section>

        <section className="ag-card">
          <h2>Bring MobileTopup to your own AI assistant</h2>
          <ul>
            <li>Works with Claude, ChatGPT and any assistant that supports MCP</li>
            <li>Your assistant finds the product and shows the price for each payment method</li>
            <li>It sends you a checkout link — <b>you confirm and pay yourself</b></li>
          </ul>
          <div className="ag-logos" aria-label="Works with Claude, ChatGPT and Cursor">
            <span>Works with</span>
            <img src={`${ST}/images/ai-icons/claude.svg`} alt="Claude" title="Claude" width="24" height="24" />
            <img src={`${ST}/images/ai-icons/openai.svg`} alt="ChatGPT" title="ChatGPT" width="24" height="24" />
            <img src={`${ST}/images/ai-icons/cursor.svg`} alt="Cursor" title="Cursor" width="24" height="24" />
          </div>
          <AgentDemo />
        </section>

        <section className="ag-card">
          <h2>Add to your assistant</h2>
          <div className="ag-apps">
            <div className="ag-app">
              <div className="ag-app-head"><span className="ag-app-icon"><img src={`${ST}/images/ai-icons/claude.svg`} alt="" width="28" height="28" /></span><h3>Claude</h3></div>
              <ul>
                <li>Settings → Connectors → Add custom connector</li>
                <li>Paste the URL, then sign in to MobileTopup</li>
              </ul>
              <CopyLine text="https://mcp.mobiletopup.com/mcp" />
            </div>
            <div className="ag-app">
              <div className="ag-app-head"><span className="ag-app-icon"><img src={`${ST}/images/ai-icons/openai.svg`} alt="" width="28" height="28" /></span><h3>ChatGPT</h3></div>
              <ul>
                <li>Settings → Apps & Connectors → Create</li>
                <li>Paste the URL, choose OAuth, then sign in to MobileTopup</li>
              </ul>
              <CopyLine text="https://mcp.mobiletopup.com/mcp" />
            </div>
          </div>
        </section>

        <section className="ag-card">
          <h2>Other assistants</h2>
          <ul>
            <li>Generate a key and paste the instructions into any assistant that supports MCP</li>
          </ul>
          {me === undefined ? <p>Loading…</p> : me ? <AgentKeyPanel me={me} /> : (
            <div className="ag-signin">
              <p>Sign in to get your agent key and instructions</p>
              <div className="ag-actions">
                <a className="ag-btn primary" href="/login?returnTo=%2Fagents">Sign in</a>
                <a className="ag-btn" href="/signup?returnTo=%2Fagents">Create a free account</a>
              </div>
            </div>
          )}
        </section>

        <section className="ag-card">
          <div className="ag-app-head">
            <h2>Coding agents</h2>
            <span className="ag-logos"><img src={`${ST}/images/ai-icons/claude.svg`} alt="Claude Code" title="Claude Code" width="22" height="22" /><img src={`${ST}/images/ai-icons/cursor.svg`} alt="Cursor" title="Cursor" width="22" height="22" /></span>
          </div>
          <ul>
            <li>Claude Code, Cursor and other agents that read skill files</li>
          </ul>
          <CopyLine text="Read https://mobiletopup.com/agents/SKILL.md" />
          <p className="ag-links"><a href="https://gitlab.com/mobiletopup/agent-files" target="_blank" rel="noreferrer">View the skill</a> · <a href="/llms.txt">llms.txt</a></p>
        </section>

        <section className="ag-card">
          <h2>Safeguards</h2>
          <div className="ag-tiles">
            <div className="ag-tile"><Glyph d={GLYPH.shield} /><span>An agent can never pay or complete a purchase</span></div>
            <div className="ag-tile"><Glyph d={GLYPH.clock} /><span>Checkout links expire after 24 hours</span></div>
            <div className="ag-tile"><Glyph d={GLYPH.lock} /><span>Every order goes through our normal account and payment checks</span></div>
          </div>
        </section>

        <section className="ag-card">
          <h2>For businesses</h2>
          <ul>
            <li><b>Partner API</b> — sell Thai top-ups, international airtime and eSIMs from your own app, paid from a prepaid balance</li>
            <li><b>Refer a friend</b> — earn wallet credit for new customers</li>
          </ul>
          <div className="ag-actions">
            <a className="ag-btn primary" href="https://partners.mobiletopup.com" target="_blank" rel="noreferrer">Partner portal</a>
            <a className="ag-btn" href="/contact">Contact us</a>
          </div>
        </section>

        <section className="ag-card">
          <h2>FAQ</h2>
          <div className="ag-faq">
            {agentsFaq.map((f) => (
              <details key={f.q}>
                <summary>{f.q}<Glyph d={GLYPH.chevron} /></summary>
                <ul>{f.a.map((x) => <li key={x}>{x}</li>)}</ul>
              </details>
            ))}
          </div>
        </section>
      </div>
    </LegalShell>
  );
}
